import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Lightbulb as TipIcon,
  QuestionAnswer as FAQIcon,
  Keyboard as ShortcutIcon,
  Book as BookIcon,
  VideoLibrary as VideoIcon,
  ContactSupport as SupportIcon,
  Timer as QuickStartIcon,
} from '@mui/icons-material';
import { usePageTitle } from '../hooks/usePageTitle';

const Help: React.FC = () => {
  usePageTitle('ヘルプ');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState<string | false>('quickstart');

  // FAQデータ
  const faqItems = [
    {
      category: '予約管理',
      questions: [
        {
          q: '予約の変更やキャンセルはどうすればいいですか？',
          a: '予約管理画面で該当の予約をクリックし、表示されるメニューから「変更」または「キャンセル」を選択してください。',
        },
        {
          q: '複数の予約を一括で処理できますか？',
          a: '予約管理画面で「複数選択モード」をオンにすると、最大20件まで同時に選択して一括処理が可能です。',
        },
        {
          q: '予約の重複を防ぐにはどうすればいいですか？',
          a: 'システムが自動的に重複をチェックし、警告を表示します。同じ時間帯に予約を入れようとすると確認画面が表示されます。',
        },
      ],
    },
    {
      category: '顧客管理',
      questions: [
        {
          q: '常連客の管理方法は？',
          a: '来店回数に応じて自動的に表示されます。10回以上（グリーン）、20回以上（ゴールド）、30回以上（プラチナ）で色分けされ、顧客ロイヤルティが一目でわかります。',
        },
        {
          q: '顧客の施術履歴を確認するには？',
          a: '顧客管理画面で顧客名をクリックし、詳細画面の「来店履歴」タブで過去の施術内容を確認できます。',
        },
      ],
    },
    {
      category: '売上分析',
      questions: [
        {
          q: '売上レポートの出力方法は？',
          a: '売上分析画面の右上にある「レポート出力」ボタンからPDFまたはExcel形式でダウンロードできます。',
        },
        {
          q: '期間を指定して分析するには？',
          a: '画面上部の日付選択フィールドで開始日と終了日を指定してください。最大1年間のデータを表示できます。',
        },
      ],
    },
  ];

  // クイックスタートガイド
  const quickStartSteps = [
    {
      step: 1,
      title: '初回ログイン',
      description: 'メールアドレスとパスワードでログインし、初期設定を完了させます。',
      time: '3分',
    },
    {
      step: 2,
      title: '基本情報の設定',
      description: '店舗情報、スタッフ情報、営業時間などを設定します。',
      time: '10分',
    },
    {
      step: 3,
      title: 'サービスメニューの登録',
      description: '提供するサービスと料金を登録します。',
      time: '15分',
    },
    {
      step: 4,
      title: '予約の受付開始',
      description: '予約管理画面から新規予約を登録してみましょう。',
      time: '5分',
    },
  ];

  // ショートカットキー
  const shortcuts = [
    { keys: 'Ctrl + N', action: '新規予約作成' },
    { keys: 'Ctrl + F', action: '検索' },
    { keys: 'Ctrl + S', action: '保存' },
    { keys: 'Esc', action: 'キャンセル・閉じる' },
    { keys: 'F1', action: 'ヘルプを開く' },
    { keys: 'Alt + D', action: 'ダッシュボードへ移動' },
    { keys: 'Alt + R', action: '予約管理へ移動' },
    { keys: 'Alt + C', action: '顧客管理へ移動' },
  ];

  // 用語集
  const glossary = [
    { term: 'リピート率', definition: '一定期間内に再来店した顧客の割合。顧客満足度の重要な指標。' },
    { term: '客単価', definition: '1回の来店あたりの平均売上金額。' },
    { term: 'カルテ', definition: '顧客の施術履歴、体調、好みなどを記録した情報。' },
    { term: 'ガントチャート', definition: '時間軸に沿って予約状況を視覚的に表示するグラフ。' },
    { term: 'バッチ処理', definition: '複数のデータを一括で処理する機能。' },
  ];

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const filteredFAQ = faqItems.map(category => ({
    ...category,
    questions: category.questions.filter(
      item => 
        item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter(category => category.questions.length > 0);

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
        ヘルプセンター
      </Typography>

      {/* 検索バー */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="質問やキーワードを入力してください..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 4 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* クイックアクセスカード */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <QuickStartIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                クイックスタート
              </Typography>
              <Typography variant="body2" color="text.secondary">
                最短で使い始める
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <VideoIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                チュートリアル動画
              </Typography>
              <Typography variant="body2" color="text.secondary">
                機能を動画で学ぶ
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <FAQIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                よくある質問
              </Typography>
              <Typography variant="body2" color="text.secondary">
                問題を素早く解決
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <SupportIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                サポート連絡
              </Typography>
              <Typography variant="body2" color="text.secondary">
                直接お問い合わせ
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* クイックスタートガイド */}
      <Accordion expanded={expandedSection === 'quickstart'} onChange={handleAccordionChange('quickstart')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <QuickStartIcon color="primary" />
            <Typography variant="h6">クイックスタートガイド</Typography>
            <Chip label="初めての方へ" size="small" color="primary" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              以下の4ステップで、約30分でサロンハブの基本機能を使い始められます。
            </Typography>
          </Alert>
          <Grid container spacing={3}>
            {quickStartSteps.map((step) => (
              <Grid item xs={12} sm={6} key={step.step}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {step.step}
                    </Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {step.title}
                    </Typography>
                    <Chip label={`約${step.time}`} size="small" variant="outlined" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* よくある質問 */}
      <Accordion expanded={expandedSection === 'faq'} onChange={handleAccordionChange('faq')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FAQIcon color="warning" />
            <Typography variant="h6">よくある質問</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {filteredFAQ.map((category) => (
            <Box key={category.category} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                {category.category}
              </Typography>
              {category.questions.map((item, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                    Q: {item.q}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                    A: {item.a}
                  </Typography>
                </Paper>
              ))}
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* キーボードショートカット */}
      <Accordion expanded={expandedSection === 'shortcuts'} onChange={handleAccordionChange('shortcuts')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShortcutIcon color="secondary" />
            <Typography variant="h6">キーボードショートカット</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            作業効率を上げるための便利なショートカットキー一覧
          </Typography>
          <Grid container spacing={2}>
            {shortcuts.map((shortcut, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip label={shortcut.keys} size="small" sx={{ fontFamily: 'monospace' }} />
                  <Typography variant="body2">{shortcut.action}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* 用語集 */}
      <Accordion expanded={expandedSection === 'glossary'} onChange={handleAccordionChange('glossary')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BookIcon color="info" />
            <Typography variant="h6">用語集</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            {glossary.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight="medium">
                        {item.term}
                      </Typography>
                    }
                    secondary={item.definition}
                  />
                </ListItem>
                {index < glossary.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </AccordionDetails>
      </Accordion>

      {/* サポート情報 */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          お困りの場合は
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SupportIcon color="primary" />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  メールサポート
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  support@salonhub.jp
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  営業日24時間以内に返信
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TipIcon color="warning" />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  操作のコツ
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  各画面の「？」アイコンをクリックすると、その場でヘルプが表示されます
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Help;